import React from 'react';
import LoadingScreen from './LoadingScreen';

// Tras un despliegue nuevo, una pestaña abierta sigue ejecutando el bundle
// anterior y pide chunks con hashes que ya no existen en el servidor. El
// resultado es un ChunkLoadError al entrar a cualquier vista lazy. Este
// boundary detecta ese caso concreto y recarga una sola vez para bajar el
// index.html nuevo. Cualquier otro error se vuelve a lanzar tal cual, para no
// alterar el manejo de errores actual de la plataforma.

const RELOAD_TIMESTAMP_KEY = 'nimbus-chunk-reload-ts';

// Si el chunk sigue faltando justo después de recargar, no insistimos: se
// muestra el aviso manual en lugar de entrar en un ciclo de recargas.
const RELOAD_COOLDOWN_MS = 30000;

export const isChunkLoadError = (error) => {
  if (!error) return false;
  if (error.name === 'ChunkLoadError') return true;

  const message = typeof error.message === 'string' ? error.message : '';
  return (
    /Loading (?:CSS )?chunk\s+\S+\s+failed/i.test(message) ||
    /Failed to fetch dynamically imported module/i.test(message)
  );
};

const readLastReload = () => {
  try {
    return parseInt(window.sessionStorage.getItem(RELOAD_TIMESTAMP_KEY), 10) || 0;
  } catch (error) {
    // sessionStorage puede no estar disponible (modo privado, iframes).
    return 0;
  }
};

const writeLastReload = (value) => {
  try {
    if (value === null) window.sessionStorage.removeItem(RELOAD_TIMESTAMP_KEY);
    else window.sessionStorage.setItem(RELOAD_TIMESTAMP_KEY, String(value));
  } catch (error) {
    // Sin sessionStorage seguimos recargando; sólo perdemos la protección
    // contra ciclos, que ya cubre el aviso manual.
  }
};

class ChunkLoadErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, status: null };
    this.handleManualReload = this.handleManualReload.bind(this);
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error) {
    if (!isChunkLoadError(error)) return;

    console.error('ChunkLoadError detectado:', error);

    const now = Date.now();
    if (now - readLastReload() > RELOAD_COOLDOWN_MS) {
      writeLastReload(now);
      this.setState({ status: 'reloading' });
      window.location.reload();
      return;
    }

    this.setState({ status: 'failed' });
  }

  handleManualReload() {
    writeLastReload(null);
    window.location.reload();
  }

  render() {
    const { error, status } = this.state;

    if (error && !isChunkLoadError(error)) {
      // No es un problema de chunks: se propaga hacia arriba sin tocarlo.
      throw error;
    }

    if (error && status === 'failed') {
      return (
        <div className='w-full h-screen flex flex-col items-center justify-center px-6 text-center'>
          <p className='text-nimbus font-bold text-lg'>No se pudo cargar esta sección</p>
          <p className='mt-2 text-sm text-gray-600 max-w-md'>
            Es posible que haya una versión nueva de la plataforma. Recarga la página para continuar.
          </p>
          <button
            type='button'
            onClick={this.handleManualReload}
            className='mt-6 px-6 py-2 bg-nimbus text-white rounded-lg hover:opacity-80 transition-opacity'
          >
            Recargar
          </button>
        </div>
      );
    }

    if (error) return <LoadingScreen mensaje='Actualizando a la versión más reciente, espere un momento' />;

    return this.props.children;
  }
}

export default ChunkLoadErrorBoundary;
